import React from 'react';
import FilterForm from 'assets/components/filters/filterForm/FilterForm';
import ThemeFilterFormDrawer from 'assets/components/filters/filterForm/themes/Theme.FilterForm.Drawer';
import Icon from 'assets/components/icon/Icon';
import DateInput from 'assets/components/inputs/dateInput/DateInput';
import SelectInput from 'assets/components/inputs/select/SelectInput';
import TextInput from 'assets/components/inputs/text/TextInput';
import InputWrapper from 'assets/components/inputs/wrappers/InputWrapper';
import { getLocales } from 'assets/locales/Locale';
import { defaultInputDateTimeFormat } from 'assets/utils/data/Date';
import { useContractProvider } from '../Contract.Hooks';
import NumberInput from 'assets/components/inputs/number/NumberInput';
import { compact, flatten, isEmpty, uniqBy } from 'lodash';
import { isDesktop } from 'react-device-detect';

export default function ContractFilters() {
  const { lang } = getLocales();
  const { listApi, statuses, dealersApi, modelsApi, userListApi, pageRouter } = useContractProvider();

  return (
    <>
      <FilterForm
        subcomponents={ThemeFilterFormDrawer}
        filters={listApi.body}
        inline={isDesktop}
        onFilter={(newFilters) =>
          pageRouter.redirect(
            { ...pageRouter.params, action: undefined },
            {
              ...pageRouter.query,
              list: {
                ...newFilters,
                offset: 0,
              },
            }
          )
        }
        onReset={() => {
          pageRouter.redirect(
            { ...pageRouter.params, action: undefined },
            {
              ...pageRouter.query,
              list: undefined,
            }
          );
        }}
        onOpen={() => {
          if (!dealersApi.payload) dealersApi.execute((body) => body);
          if (!modelsApi.payload) modelsApi.execute((body) => body);
        }}
        render={({ tempFilters, setFilters }) => {
          const dealers = dealersApi?.payload?.data ?? [];

          const users = userListApi?.payload?.data ?? [];

          const products = !isEmpty(tempFilters?.dealerId)
            ? flatten(dealers.filter((d) => tempFilters?.dealerId?.some((id) => id === d.id)).map((d) => d.products))
            : uniqBy(flatten(dealers.map((d) => d.products)), (p) => p.id);

          const makes =
            compact(
              uniqBy(
                modelsApi?.payload?.data?.map((m) => m.vehicleMake),
                (m) => m.id
              )
            ) ?? [];

          const models =
            modelsApi?.payload?.data?.filter((m) =>
              tempFilters?.makeCode?.some((code) => code === m.vehicleMake?.makeCode)
            ) ?? [];

          return (
            <>
              <TextInput
                name="contractNumber"
                label={lang.contractNumber}
                value={tempFilters?.contractNumber}
                onChange={({ contractNumber }) => setFilters({ ...tempFilters, contractNumber })}
                icon={<Icon class="if-quill-pen" />}
              />
              <SelectInput
                name="createdById"
                label={lang.createdBy}
                data={users?.map((user) => ({
                  id: user.id,
                  title: user.fullName,
                  icon: <Icon class="fas-user" />,
                }))}
                isMultiselect
                disableTags={isDesktop}
                value={tempFilters?.createdById}
                onChange={({ createdById }) => setFilters({ ...tempFilters, createdById })}
                icon={<Icon class="fas-user" />}
              />
              <SelectInput
                name="statusCode"
                label={lang.status}
                data={statuses.map((item) => ({
                  id: item.id,
                  title: item.title,
                  icon: <Icon class="fas-check" />,
                }))}
                isMultiselect
                disableTags={isDesktop}
                value={tempFilters?.statusCode}
                onChange={({ statusCode }) => setFilters({ ...tempFilters, statusCode })}
                icon={<Icon class="fas-cloud" />}
              />
              <TextInput
                name="vin"
                label={lang.vin}
                value={tempFilters?.vin}
                onChange={({ vin }) => setFilters({ ...tempFilters, vin })}
                icon={<Icon class="if-quill-pen" />}
              />
              <NumberInput
                name="vehicleYear"
                label={lang.vehicleYear}
                value={tempFilters?.vehicleYear}
                onChange={({ vehicleYear }) => setFilters({ ...tempFilters, vehicleYear })}
                icon={<Icon class="if-quill-pen" />}
              />
              <SelectInput
                name="makeCode"
                label={lang.vehicleMake}
                data={makes.map((item) => ({
                  id: item.makeCode,
                  title: item.makeCode,
                  icon: <Icon class="fas-cloud" />,
                }))}
                isMultiselect
                disableTags={isDesktop}
                value={tempFilters?.makeCode}
                disabled={!makes.length}
                onChange={({ makeCode }) => setFilters({ ...tempFilters, makeCode })}
                icon={<Icon class="fas-cloud" />}
              />
              <SelectInput
                name="vehicleModelId"
                label={lang.vehicleModel}
                data={models.map((item) => ({
                  ...item.displayInfo,
                  icon: <Icon class="fas-cloud" />,
                }))}
                isMultiselect
                disableTags={isDesktop}
                value={tempFilters?.vehicleModelId}
                disabled={!models.length}
                onChange={({ vehicleModelId }) => setFilters({ ...tempFilters, vehicleModelId })}
                icon={<Icon class="fas-cloud" />}
              />
              <TextInput
                name="clientName"
                label={lang.clientName}
                value={tempFilters?.clientName}
                onChange={({ clientName }) => setFilters({ ...tempFilters, clientName })}
                icon={<Icon class="if-quill-pen" />}
              />
              <SelectInput
                name="dealerId"
                label={lang.dealers}
                data={dealers.map((item) => ({
                  ...item.displayInfo,
                  icon: <Icon class="fas-cloud" />,
                }))}
                isMultiselect
                disableTags={isDesktop}
                value={tempFilters?.dealerId}
                disabled={!dealers.length}
                onChange={({ dealerId }) => setFilters({ ...tempFilters, dealerId })}
                icon={<Icon class="fas-cloud" />}
              />
              <SelectInput
                name="productId"
                label={lang.products}
                data={products.map((item) => ({
                  ...item.displayInfo,
                  icon: <Icon class="fas-box" />,
                }))}
                isMultiselect
                disableTags={isDesktop}
                value={tempFilters?.productId}
                onChange={({ productId }) => setFilters({ ...tempFilters, productId })}
                icon={<Icon class="fas-box" />}
              />
              <InputWrapper style={{ gridColumn: 'span 2', height: 'fit-content', marginTop: 'var(--sizeM)' }}>
                <DateInput
                  name="expiryFromDateUtc"
                  label={lang.expiryFromDate}
                  dateTimeFormat={defaultInputDateTimeFormat}
                  value={tempFilters?.expiryFromDateUtc ?? null}
                  onChange={({ expiryFromDateUtc }) => setFilters({ ...tempFilters, expiryFromDateUtc })}
                  icon={<Icon class="fas-calendar-alt" />}
                />
                <DateInput
                  name="expiryToDateUtc"
                  label={lang.expiryToDate}
                  dateTimeFormat={defaultInputDateTimeFormat}
                  value={tempFilters?.expiryToDateUtc ?? null}
                  onChange={({ expiryToDateUtc }) => setFilters({ ...tempFilters, expiryToDateUtc })}
                  icon={<Icon class="fas-calendar-alt" />}
                />
              </InputWrapper>
              <InputWrapper style={{ gridColumn: 'span 2', height: 'fit-content', marginTop: 'var(--sizeM)' }}>
                <DateInput
                  name="effectiveFromDateUtc"
                  label={lang.effectiveFromDate}
                  dateTimeFormat={defaultInputDateTimeFormat}
                  value={tempFilters?.effectiveFromDateUtc ?? null}
                  onChange={({ effectiveFromDateUtc }) => setFilters({ ...tempFilters, effectiveFromDateUtc })}
                  icon={<Icon class="fas-calendar-alt" />}
                />
                <DateInput
                  name="effectiveToDateUtc"
                  label={lang.effectiveToDate}
                  dateTimeFormat={defaultInputDateTimeFormat}
                  value={tempFilters?.effectiveToDateUtc ?? null}
                  onChange={({ effectiveToDateUtc }) => setFilters({ ...tempFilters, effectiveToDateUtc })}
                  icon={<Icon class="fas-calendar-alt" />}
                />
              </InputWrapper>
              <InputWrapper style={{ gridColumn: 'span 2', height: 'fit-content', marginTop: 'var(--sizeM)' }}>
                <DateInput
                  name="createdAtFromDateUtc"
                  label={lang.createdAtFromDate}
                  dateTimeFormat={defaultInputDateTimeFormat}
                  value={tempFilters?.createdAtFromDateUtc}
                  onChange={({ createdAtFromDateUtc }) => setFilters({ ...tempFilters, createdAtFromDateUtc })}
                  icon={<Icon class="fas-calendar-alt" />}
                />
                <DateInput
                  name="createdAtToDateUtc"
                  label={lang.createdAtToDate}
                  dateTimeFormat={defaultInputDateTimeFormat}
                  value={tempFilters?.createdAtToDateUtc}
                  onChange={({ createdAtToDateUtc }) => setFilters({ ...tempFilters, createdAtToDateUtc })}
                  icon={<Icon class="fas-calendar-alt" />}
                />
              </InputWrapper>
              <InputWrapper style={{ gridColumn: 'span 2', height: 'fit-content', marginTop: 'var(--sizeM)' }}>
                <DateInput
                  name="modifiedAtFromDateUtc"
                  label={lang.modifiedAtFromDate}
                  dateTimeFormat={defaultInputDateTimeFormat}
                  value={tempFilters?.modifiedAtFromDateUtc}
                  onChange={({ modifiedAtFromDateUtc }) => setFilters({ ...tempFilters, modifiedAtFromDateUtc })}
                  icon={<Icon class="fas-calendar-alt" />}
                />
                <DateInput
                  name="modifiedAtToDateUtc"
                  label={lang.modifiedAtToDate}
                  dateTimeFormat={defaultInputDateTimeFormat}
                  value={tempFilters?.modifiedAtToDateUtc}
                  onChange={({ modifiedAtToDateUtc }) => setFilters({ ...tempFilters, modifiedAtToDateUtc })}
                  icon={<Icon class="fas-calendar-alt" />}
                />
              </InputWrapper>
            </>
          );
        }}
      />
    </>
  );
}
